import {action, computed, makeObservable, observable, runInAction} from "mobx";
import ApiResponseHandler from "@eryxcoop/appyx-comm/src/errors/ApiResponseHandler";

export default class ChatWidgetFeature {

    constructor(application) {
        this._application = application;
        this._loaded = false;
        this._isOpen = false;
        this._isTyping = false;
        this._draft = "";
        this._nextMessageId = 1;
        this._messages = this._defaultMessages();


        makeObservable(this, {
            _loaded: observable,
            loaded: computed,
            _isOpen: observable,
            _isTyping: observable,
            _draft: observable,
            _messages: observable,
            isOpen: computed,
            isTyping: computed,
            draft: computed,
            messages: computed,
            toggleOpen: action,
            open: action,
            close: action,
            updateDraft: action,
            load: action,
            finishLoading: action,
            sendMessage: action,
            _appendMessage: action,
            _setMessages: action,
        });
    }

    finishLoading() {
        this._loaded = true;
    }

    get loaded() {
        return this._loaded;
    }

    set loaded(value) {
        this._loaded = value;
    }

    load() {
        this._loadMessagesFromStorage();
        this.finishLoading();
    }

    get isOpen() {
        return this._isOpen;
    }

    get isTyping() {
        return this._isTyping;
    }

    get draft() {
        return this._draft;
    }

    get messages() {
        return this._messages;
    }

    toggleOpen() {
        this._isOpen = !this._isOpen;
    }

    open() {
        this._isOpen = true;
    }

    close() {
        this._isOpen = false;
    }

    updateDraft(value) {
        this._draft = value;
    }

    async sendMessage() {
        const message = this._draft.trim();
        if (!message) {
            return false;
        }
        this._appendMessage({
            author: "user",
            content: message,
        });
        this._draft = "";
        const customer = this._application?.getCustomer?.();
        if (!customer || !customer.businessName) {
            this._appendMessage({
                author: "assistant",
                content: "Necesito que inicies sesión para poder ayudarte con pedidos y consultas.",
            });
            return false;
        }

        const history = this._historyPayload();
        const sessionId = this._application.session().chatSessionId();
        this._isTyping = true;
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: async (response) => {
                runInAction(() => {
                    this._isTyping = false;
                    this._appendMessage({
                        author: "assistant",
                        content: response.answer(),
                    });
                });
            },
            handlesError: () => {
                runInAction(() => {
                    this._isTyping = false;
                    this._appendMessage({
                        author: "assistant",
                        content: "Tuve un problema al consultar el asistente. Probá de nuevo en unos minutos.",
                    });
                });
            }
        });

        try {
            await this._application.apiClient().askMarketplaceAssistant(
                message,
                customer.businessName,
                history,
                sessionId,
                responseHandler
            );
        } catch (error) {
            runInAction(() => {
                this._isTyping = false;
                this._appendMessage({
                    author: "assistant",
                    content: "Tuve un problema al consultar el asistente. Probá de nuevo en unos minutos.",
                });
            });
        }
        return true;
    }

    _appendMessage(message) {
        this._messages.push(this._createMessage(message.author, message.content));
        this._saveHistory();
    }

    _loadMessagesFromStorage() {
        const storedMessages = this._application.session().chatHistory();
        if (storedMessages.length > 0) {
            this._setMessages(storedMessages);
            return;
        }
        this._setMessages(this._defaultMessages());
    }

    _setMessages(messages) {
        this._messages = messages;
        this._nextMessageId = this._nextMessageIdFrom(messages);
        this._saveHistory();
    }

    _nextMessageIdFrom(messages) {
        if (!messages.length) {
            return 1;
        }
        const maxId = messages.reduce((currentMax, message) => {
            const messageId = Number.isFinite(message.id) ? message.id : 0;
            return Math.max(currentMax, messageId);
        }, 0);
        return maxId + 1;
    }

    _historyPayload() {
        const allMessages = this._messages.map((message) => ({
            role: message.author === "user" ? "user" : "assistant",
            content: message.content,
        }));
        return allMessages.slice(-5);
    }

    _saveHistory() {
        this._application.session().saveChatHistory(this._messages);
    }

    _defaultMessages() {
        return [
            this._createMessage("assistant", "Hola, soy el asistente de Sapore."),
            this._createMessage("assistant", "Podés consultar sobre pedidos, cuentas corrientes y artículos."),
        ];
    }

    _createMessage(author, content) {
        return {
            id: this._nextMessageId++,
            author: author,
            content: content,
            createdAt: new Date(),
        };
    }
}
